import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  Droplets,
  ShieldCheck,
  Clock,
  Leaf,
  Sparkles,
  Package,
  Wind,
  Truck,
  BadgeCheck,
  Phone,
} from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../../lib/contact";

const PRODUCT_1_IMAGE =
  "https://customer-assets.emergentagent.com/job_ba139885-dbe0-4ef6-b24d-8e4c6876b21d/artifacts/92be607t_image.png";
const PRODUCT_2_IMAGE =
  "https://customer-assets.emergentagent.com/job_ba139885-dbe0-4ef6-b24d-8e4c6876b21d/artifacts/gdwtna1o_6a06b373-a920-4c9a-b908-f53bf6924530.png";

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.11 4.92A10 10 0 0 0 4.07 18.2L3 22l3.88-1.03A10 10 0 1 0 19.11 4.92Zm-7.1 15.34a8.27 8.27 0 0 1-4.21-1.15l-.3-.18-2.3.61.62-2.24-.2-.32a8.27 8.27 0 1 1 6.4 3.28Zm4.55-6.2c-.25-.12-1.47-.72-1.7-.8-.23-.09-.39-.13-.56.12-.16.24-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23a7.5 7.5 0 0 1-1.38-1.72c-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.84-.2-.49-.41-.42-.56-.43h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.6.13.17 1.76 2.69 4.27 3.77 1.49.64 2.07.7 2.81.59.45-.07 1.47-.6 1.68-1.19.21-.58.21-1.08.15-1.18-.07-.1-.23-.16-.49-.28Z" />
  </svg>
);

const PRODUCTS = [
  {
    id: "halipak-hurcu",
    badge: "Ürün 01 · Ana Ürün · Ambalaj",
    name: "HALIPAK",
    sub: "Halı Hurcu",
    desc:
      "Halı yıkama firmalarına özel olarak tasarlanmış, dayanıklı ve %100 çevreci halı paketleme ambalajımız. Halılarınızı darbe, koku ve neme karşı korur; profesyonel görünüm kazandırır. HALIPAK'ın ana ürünüdür.",
    image: PRODUCT_2_IMAGE,
    features: [
      { icon: Leaf, title: "%100 Çevreci", desc: "Doğaya duyarlı paketleme malzemesi." },
      { icon: BadgeCheck, title: "Yüksek Kalite", desc: "Dayanıklı ve güvenilir yapı." },
      { icon: Package, title: "Güçlü Paketleme", desc: "Darbelere ve kirlenmeye karşı korur." },
      { icon: Wind, title: "Koku & Nem Koruması", desc: "Özel ambalaj ile içerideki halıyı korur." },
      { icon: Truck, title: "Türkiye'nin Her Yerine", desc: "Hızlı ve güvenli gönderim." },
    ],
    align: "right",
  },
  {
    id: "proclean",
    badge: "Ürün 02 · Kimya",
    name: "PROCLEAN",
    sub: "Halı Leke Çıkarıcı",
    desc:
      "Güçlü formülü ile zorlu lekeleri derinlemesine söker, halınızın dokusuna zarar vermeden ilk günkü tertemiz görünümüne kavuşturur. Profesyonel halı yıkamacılar için geliştirilmiştir.",
    image: PRODUCT_1_IMAGE,
    features: [
      { icon: Droplets, title: "Güçlü Formül", desc: "Zorlu lekeleri etkili şekilde çıkarır." },
      { icon: ShieldCheck, title: "Halıya Zarar Vermez", desc: "Dokuyu korur, yıpratmadan temizler." },
      { icon: Clock, title: "Hızlı Etki", desc: "Kısa sürede sonucunu gösterir." },
      { icon: Leaf, title: "Ferah Koku", desc: "Temizlik sonrası ferahlık bırakır." },
      { icon: Sparkles, title: "Profesyonel Sonuç", desc: "Uzman kalitesinde temizlik." },
    ],
    align: "left",
  },
];

const ProductCard = ({ product, index }) => {
  const imageOnRight = product.align === "right";

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.id}`}
      className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
    >
      {/* Image side */}
      <div className={`lg:col-span-5 ${imageOnRight ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#2563EB]/15 to-[#0A192F]/5 blur-2xl rounded-[2.5rem]" />
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-xl shadow-[#0A192F]/10">
            <img
              src={product.image}
              alt={`${product.name} ${product.sub}`}
              className="w-full h-full object-contain p-2 sm:p-3"
              loading="lazy"
              data-testid={`product-image-${product.id}`}
            />
          </div>
          <div className="absolute -top-3 -left-3 bg-[#0A192F] text-white rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest shadow-lg">
            {product.badge}
          </div>
        </div>
      </div>

      {/* Text side */}
      <div className={`lg:col-span-7 ${imageOnRight ? "lg:order-1" : "lg:order-2"}`}>
        <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#2563EB]">
          {product.id === "halipak-hurcu" ? "Ambalaj Çözümleri · Ana Ürün" : "Kimya Çözümleri"}
        </div>
        <h3 className="mt-3 font-heading font-extrabold text-[#0A192F] text-4xl sm:text-5xl leading-[1.02] tracking-tight">
          {product.name}{" "}
          <span className="text-slate-400 font-medium">/ {product.sub}</span>
        </h3>
        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
          {product.desc}
        </p>

        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
          {product.features.map((f) => {
            const Icon = f.icon;
            return (
              <li
                key={f.title}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#2563EB]/40 hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 grid place-items-center shrink-0">
                  <Icon className="w-5 h-5 text-[#2563EB]" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-semibold text-[#0A192F] text-sm">{f.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {f.desc}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid={`product-${product.id}-phone-cta`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold hover:bg-[#112240] active:scale-95 transition-all"
          >
            <Phone className="w-4 h-4" /> Sipariş için Ara
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-${product.id}-whatsapp-cta`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] text-white px-6 py-3 text-sm font-semibold hover:bg-[#0FA978] active:scale-95 transition-all"
          >
            <WhatsAppIcon className="w-4 h-4" /> WhatsApp ile Bilgi Al
          </a>
        </div>

        <div className="mt-4 text-xs text-slate-500 inline-flex items-center gap-2">
          <Check className="w-3.5 h-3.5 text-emerald-500" />
          Toptan satış ve halı yıkama firmalarına özel fiyat
        </div>
      </div>
    </motion.article>
  );
};

const Products = () => {
  return (
    <section
      id="urunler"
      data-testid="products-section"
      className="py-20 lg:py-28 bg-[#F8FAFC]"
    >
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl">
          <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#2563EB]">
            Ürünlerimiz
          </div>
          <h2 className="mt-4 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[1.02] tracking-tight">
            İki ürün, <span className="text-[#2563EB]">tek profesyonel</span> standart.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl">
            Halı yıkama sürecinin temizlik ve teslim aşamalarını eksiksiz tamamlayan
            iki HALIPAK ürünüyle tanışın.
          </p>
        </div>

        <div className="mt-16 lg:mt-20 space-y-24 lg:space-y-32">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
